// src/lib/useCSSSlice.js - FIXED VERSION WITH APP MODE SUPPORT
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const useCSSSlice = (animationType = 'auto') => {
  const location = useLocation();
  const lastPathRef = useRef('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Determine what mode we should be in based on path
  const getModeForPath = (path) => {
    const clayRoutes = ['/', '/pricing', '/company', '/privacy-policy', '/terms-of-service'];
    const signupPaths = ['/signup', '/pending-approval'];
    
    if (clayRoutes.includes(path)) return 'clay';
    if (signupPaths.some(p => path.startsWith(p))) return 'signup';
    return 'app'; // Default for all other routes (dashboard, settings, etc.)
  };

  // Check if this transition should be animated
  const shouldAnimate = (fromPath, toPath) => {
    // Get modes for both paths
    const fromMode = getModeForPath(fromPath);
    const toMode = getModeForPath(toPath);
    
    // Only animate when transitioning between different modes
    // AND only for specific transitions (not all)
    if (fromMode === toMode) return false;
    
    // Specific animation rules:
    // 1. Animate clay -> signup (going to login)
    // 2. Animate signup -> clay (going back to landing)
    // 3. Don't animate to/from app mode (dashboard, etc.)
    return (fromMode === 'clay' && toMode === 'signup') || 
           (fromMode === 'signup' && toMode === 'clay');
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = lastPathRef.current;
    
    // Skip if same path
    if (currentPath === previousPath) return;
    
    // Determine the target mode
    const targetMode = getModeForPath(currentPath);
    const needsAnimation = shouldAnimate(previousPath, currentPath);
    
    const performTransition = async () => {
      if (needsAnimation) {
        console.log(`🎬 Animating transition: ${previousPath} → ${currentPath}`);
        setIsTransitioning(true);
      }
      
      try {
        const manager = window.__clayPreloadManager;
        
        switch (targetMode) {
          case 'signup':
            if (needsAnimation && manager?.disableClayStyles) {
              await manager.disableClayStyles(animationType);
            } else {
              disableClayCSS('signup');
            }
            break;
            
          case 'clay':
            if (needsAnimation && manager?.enableClayStyles) {
              await manager.enableClayStyles(animationType);
            } else {
              enableClayCSS();
            }
            break;
            
          case 'app':
            // For app mode, just ensure Clay styles are disabled
            // Don't override the body class - let App.js handle it
            disableClayStyles();
            // IMPORTANT: Don't set body class here!
            break;
        }
      } catch (error) {
        console.error('Transition failed:', error);
        // Fallback based on target mode
        if (targetMode === 'signup') {
          disableClayCSS('signup');
        } else if (targetMode === 'clay') {
          enableClayCSS();
        } else {
          disableClayStyles();
        }
      } finally {
        if (needsAnimation) {
          setIsTransitioning(false);
        }
      }
    };

    lastPathRef.current = currentPath;
    performTransition();
  }, [location, animationType]);

  // Disable Clay styles without changing body class (for app mode)
  const disableClayStyles = () => {
    const clayStyles = document.querySelectorAll('style, link');
    clayStyles.forEach(style => {
      const content = style.textContent || style.href || '';
      if (content.includes('fs-clay') || 
          content.includes('clay-landing-wrapper') ||
          content.includes('--fonts--body-font') ||
          content.includes('var(--_swatches') ||
          (style.href && style.href.includes('clay'))) {
        style.disabled = true;
        style.setAttribute('data-clay-disabled', 'true');
      }
    });
    // Don't set body class - let App.js handle it!
  };

  // Fallback function to disable Clay CSS and set mode
  const disableClayCSS = (mode) => {
    disableClayStyles();
    // Only set body class if not app mode
    if (mode === 'signup') {
      document.body.className = 'signup-mode';
      document.documentElement.className = 'signup-mode';
      document.documentElement.setAttribute('data-mode', 'signup');
    }
    // For app mode, don't set anything here
  };

  // Fallback function to enable Clay CSS
  const enableClayCSS = () => {
    const disabledClayStyles = document.querySelectorAll('[data-clay-disabled="true"]');
    disabledClayStyles.forEach(style => {
      style.disabled = false;
      style.removeAttribute('data-clay-disabled');
    });
    document.body.className = 'clay-mode';
    document.documentElement.className = 'clay-mode';
    document.documentElement.setAttribute('data-mode', 'clay');
  };

  return { isTransitioning };
};

export default useCSSSlice;